import { createEffect } from 'effector';
import {
  createReportWorker,
  proxyCallback,
  type ReportDocument,
  type ReportWorker,
} from '../../worker';

/*
 * Hosts the PDF report worker (docs/PLAN.md M9). One export at a time: starting another or
 * cancelling terminates the worker, the only way to stop it mid-layout.
 */

export class ReportCancelledError extends Error {
  constructor() {
    super('Report cancelled');
    this.name = 'ReportCancelledError';
  }
}

let worker: ReportWorker | null = null;
let rejectRender: ((error: Error) => void) | null = null;

function stopRender(): void {
  if (!rejectRender) return;
  rejectRender(new ReportCancelledError());
  rejectRender = null;
  worker?.terminate();
  worker = null;
}

/** Lays the report out as a PDF in the worker; progress is the share of sections done. */
export const renderReportFx = createEffect(
  ({
    report,
    onProgress,
  }: {
    report: ReportDocument;
    onProgress: (fraction: number) => void;
  }): Promise<Uint8Array> =>
    new Promise<Uint8Array>((resolve, reject) => {
      stopRender();
      const current = (worker ??= createReportWorker());
      rejectRender = reject;
      current.api.render(report, proxyCallback(onProgress)).then(
        (pdf) => {
          rejectRender = null;
          resolve(pdf);
        },
        (error: unknown) => {
          rejectRender = null;
          current.terminate();
          if (worker === current) worker = null;
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    }),
);

export const cancelReportFx = createEffect(stopRender);
